import { useEffect, useState } from 'react';

/**
 * Componente para anunciar mensagens para leitores de tela (VoiceOver/TalkBack)
 * 
 * Uso:
 * ```tsx
 * const { announce } = useScreenReaderAnnouncer();
 * announce('Corrida iniciada', 'polite');
 * announce('Erro crítico!', 'assertive');
 * ```
 */

interface AnnouncerProps {
  /** Tipo de anúncio - assertive interrompe, polite aguarda */
  politeness?: 'polite' | 'assertive';
  children?: React.ReactNode;
}

/**
 * Região ARIA Live para anúncios de leitores de tela
 */
export function ScreenReaderAnnouncer({ politeness = 'polite', children }: AnnouncerProps) {
  return (
    <div
      role="status"
      aria-live={politeness}
      aria-atomic="true"
      className="sr-only"
    >
      {children}
    </div>
  );
}

/**
 * Hook para criar anúncios dinâmicos para leitores de tela
 */
export function useScreenReaderAnnouncer() {
  const [message, setMessage] = useState('');
  const [politeness, setPoliteness] = useState<'polite' | 'assertive'>('polite');

  const announce = (text: string, level: 'polite' | 'assertive' = 'polite') => {
    setPoliteness(level);
    setMessage(''); // Limpa para forçar re-anúncio
    
    setTimeout(() => {
      setMessage(text);
    }, 100);

    // Auto-limpa após 3 segundos
    setTimeout(() => {
      setMessage('');
    }, 3000);
  };

  const Announcer = () => (
    <div
      role="status"
      aria-live={politeness}
      aria-atomic="true"
      className="sr-only"
    >
      {message}
    </div>
  );

  return { announce, Announcer };
}

/**
 * Hook para anunciar mudanças de rota para leitores de tela
 */
export function useRouteAnnouncer(routeName: string) {
  useEffect(() => {
    // Anuncia a nova página após navegação
    const timer = setTimeout(() => {
      const announcer = document.getElementById('route-announcer');
      if (announcer) {
        announcer.textContent = `Navegou para ${routeName}`;
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [routeName]);
}

/**
 * Componente global para anúncios de rota
 * Deve ser colocado no App.tsx
 */
export function GlobalRouteAnnouncer() {
  return (
    <div
      id="route-announcer"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    />
  );
}
